'use client';

import React, { FC } from 'react';

import classNames from 'classnames';
import { Entry } from 'contentful';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { DynamicComponent, Seo } from '@/components';
import Heading, { HeadingTag } from '@/components/ui/Heading/Heading';

import { buildCanonicalUrl } from '@/core/contentfulConfig';
import { FeatureFlag } from '@/core/featureFlags';
import { TypeProductLandingPage } from '@/core/generated-types';

const ERC_LP_ID = '10bp7cWprTs8H3xjIn8snB';
// Component entry IDs to hide when feature flag variant B is active
const HIDDEN_COMPONENT_ENTRY_IDS: string[] = [
  '1Hz4l6XGyLQa1fcn6YMLiQ',
  '4PEMcEtMI36P8ZglwYCtU8',
];

const ProductLandingPageLayout: FC<TypeProductLandingPage> = (page) => {
  const {
    fields: {
      title,
      components,
      description,
      enableInternalSticky,
      categoryLPDesign,
    },
  } = page;

  const metadataFields = page.fields?.seoMetadata?.fields;

  // Get the feature flag value
  const {
    [FeatureFlag.SUB_SQUAD_ERC_LP_HIDE_VALUE_PROP]: ercValuePropHideVariant,
  } = useFlags();

  // Determine if we should hide specific components (variant B)
  const shouldHideComponents =
    ercValuePropHideVariant === 'b' && page.sys.id === ERC_LP_ID;

  // Helper function to check if component should be hidden
  const shouldHideComponent = (
    component: Entry<Record<string, any>>,
  ): boolean => {
    // Hide components with specific entry ids
    return HIDDEN_COMPONENT_ENTRY_IDS.includes(component.sys.id);
  };

  // Filter components based on the feature flag
  const filteredComponents = shouldHideComponents
    ? components?.filter((component) => !shouldHideComponent(component))
    : components;

  return (
    <>
      <Seo
        title={metadataFields?.pageTitle}
        description={metadataFields?.pageDescription}
        nofollow={metadataFields?.noFollow}
        noindex={metadataFields?.noIndex}
        canonical={buildCanonicalUrl(page)}
      />
      <div
        className={classNames({
          'overflow-hidden': !enableInternalSticky,
          'text-center': categoryLPDesign,
        })}
      >
        {title && (
          <Heading
            tag={HeadingTag.H1}
            classes={classNames('container mx-auto', {
              'text-h-x3 mt-8': !categoryLPDesign,
              'text-h-x1 mt-20 mb-4': categoryLPDesign,
            })}
            align="center"
            text={title}
            hoverEffect
          />
        )}

        {description && (
          <p
            className={classNames('container mx-auto', {
              'text-lg pb-7 md:pb-12 border-b border-b-grey-mid-light':
                categoryLPDesign,
            })}
          >
            {description}
          </p>
        )}

        <div>
          {filteredComponents?.map((component) => (
            <DynamicComponent key={component.sys.id} component={component} />
          ))}
        </div>
      </div>
    </>
  );
};

export default ProductLandingPageLayout;
